import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

// Interactive SVG Illustration of geometric characters reacting to input states
const RenderIllustration = ({ focusedField, showPassword, passwordLength, usernameLength = 0, isLoading = false, hasError = false }) => {
  let purpleEyeOffsetLeft = 2;
  let purpleEyeOffsetTop = -1;
  
  let orangeEyeOffsetLeft = 2;
  let orangeEyeOffsetTop = -2;

  let blackEyeOffsetLeft = 2;
  let blackEyeOffsetTop = -1;

  if (hasError) {
    // Dizzy/sad look on error
    purpleEyeOffsetLeft = -3;
    purpleEyeOffsetTop = -3;
    orangeEyeOffsetLeft = -2;
    orangeEyeOffsetTop = -3;
    blackEyeOffsetLeft = -2;
    blackEyeOffsetTop = -3;
  } else if (isLoading) {
    // Excited/upward look when logging in
    purpleEyeOffsetLeft = 2;
    purpleEyeOffsetTop = -4;
    orangeEyeOffsetLeft = 3;
    orangeEyeOffsetTop = -4;
    blackEyeOffsetLeft = 2;
    blackEyeOffsetTop = -4;
  } else if (showPassword) {
    // Always look away if the password is unmasked
    purpleEyeOffsetLeft = -10;
    purpleEyeOffsetTop = -1;
    
    orangeEyeOffsetLeft = -8;
    orangeEyeOffsetTop = -2.5;
    
    blackEyeOffsetLeft = -8;
    blackEyeOffsetTop = -2.5;
  } else if (focusedField === 'username') {
    // Dynamic tracking of the username cursor position based on input length
    purpleEyeOffsetLeft = Math.min(2 + usernameLength * 0.4, 9);
    purpleEyeOffsetTop = 2.5;
    
    orangeEyeOffsetLeft = Math.min(2 + usernameLength * 0.4, 8);
    orangeEyeOffsetTop = -1;
    
    blackEyeOffsetLeft = Math.min(2 + usernameLength * 0.4, 8);
    blackEyeOffsetTop = 1.5;
  } else if (focusedField === 'password') {
    // Progressive tracking: look right towards password input and slide deeper as characters are typed
    const trackOffset = Math.min(6 + passwordLength * 0.3, 11);
    purpleEyeOffsetLeft = trackOffset;
    purpleEyeOffsetTop = 2.5;

    orangeEyeOffsetLeft = Math.min(6 + passwordLength * 0.3, 10);
    orangeEyeOffsetTop = -1.5;

    blackEyeOffsetLeft = Math.min(6 + passwordLength * 0.3, 10);
    blackEyeOffsetTop = 2.5;
  }

  let purpleTransform = 'none';
  let blackTransform = 'none';
  let yellowTransform = 'none';
  let orangeTransform = 'none';

  if (hasError) {
    // Permanent worried/sad skew when error is showing
    purpleTransform = 'skewX(-4deg) translate(-5px, 0px)';
    blackTransform = 'skewX(-2deg) translate(-3px, 0px)';
    yellowTransform = 'skewX(-2deg) translate(-3px, 0px)';
    orangeTransform = 'skewX(-2deg) translate(-3px, 0px)';
  } else if (isLoading) {
    purpleTransform = 'none';
    blackTransform = 'none';
    yellowTransform = 'none';
    orangeTransform = 'none';
  } else if (showPassword) {
    // Look away state: slightly tilted left
    purpleTransform = 'skewX(-10deg) translate(-18px, 0px)';
    blackTransform = 'skewX(-5deg) translate(-8px, 0px)';
    yellowTransform = 'none';
    orangeTransform = 'none';
  } else if (focusedField === 'username') {
    // Dynamic skew: Leans in further as the user types more characters
    const lean = Math.min(2 + usernameLength * 0.4, 9);
    purpleTransform = `skewX(${lean}deg) translate(${lean * 1.5}px, 0px)`;
    blackTransform = `skewX(${lean * 0.7}deg) translate(${lean}px, 0px)`;
    yellowTransform = `skewX(${lean * 0.5}deg) translate(${lean * 0.7}px, 0px)`;
    orangeTransform = `skewX(${lean * 0.5}deg) translate(${lean * 0.7}px, 0px)`;
  } else if (focusedField === 'password') {
    // Progressive lean to the right: leans deeper as password is typed (no upright snap/jump)
    const lean = Math.min(8 + passwordLength * 0.3, 13);
    purpleTransform = `skewX(${lean}deg) translate(${lean * 1.8}px, 0px)`;
    blackTransform = `skewX(${lean * 0.7}deg) translate(${lean * 1.2}px, 0px)`;
    yellowTransform = `skewX(${lean * 0.4}deg) translate(${lean * 0.8}px, 0px)`;
    orangeTransform = `skewX(${lean * 0.3}deg) translate(${lean * 0.6}px, 0px)`;
  }

  // Determine group animation classes (applied to inner groups to prevent overriding layout skew/tilt)
  let purpleInnerClass = "purple-idle";
  let blackInnerClass = "black-idle";
  let yellowInnerClass = "yellow-idle";
  let orangeInnerClass = "orange-idle";

  if (isLoading) {
    purpleInnerClass = "purple-loading";
    blackInnerClass = "black-loading";
    yellowInnerClass = "yellow-loading";
    orangeInnerClass = "orange-loading";
  } else if (hasError) {
    purpleInnerClass = "error-shaking";
    blackInnerClass = "error-shaking";
    yellowInnerClass = "error-shaking";
    orangeInnerClass = "error-shaking";
  }

  const purpleInnerStyle = { transformOrigin: '160px 320px' };
  const blackInnerStyle = { transformOrigin: '245px 320px' };
  const yellowInnerStyle = { transformOrigin: '310px 320px' };
  const orangeInnerStyle = { transformOrigin: '130px 320px' };

  // Mouth and Beak state styles for cross-fading (avoiding pops/swapping jumps)
  const isPurpleMouthError = hasError;
  const isPurpleMouthLoading = isLoading;
  const isPurpleMouthPasswordTyping = focusedField === 'password' && !showPassword && passwordLength > 0;
  const isPurpleMouthShowPassword = showPassword;
  const isPurpleMouthDefault = !isPurpleMouthError && !isPurpleMouthLoading && !isPurpleMouthPasswordTyping && !isPurpleMouthShowPassword;

  const getPurpleMouthStyle = (isActive) => ({
    opacity: isActive ? 1 : 0,
    transform: isActive ? 'scale(1)' : 'scale(0.3)',
    transformOrigin: '160px 148px',
    transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1), transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)'
  });

  const getBlackMouthStyle = (isActive) => ({
    opacity: isActive ? 1 : 0,
    transform: isActive ? 'scale(1)' : 'scale(0.3)',
    transformOrigin: '245px 198px',
    transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1), transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)'
  });

  const isYellowBeakError = hasError;
  const isYellowBeakPassword = focusedField === 'password' && !showPassword;
  const isYellowBeakDefault = !isYellowBeakError && !isYellowBeakPassword;

  const getYellowBeakStyle = (isActive) => ({
    opacity: isActive ? 1 : 0,
    transform: isActive ? 'scale(1)' : 'scale(0.4)',
    transformOrigin: '342px 233px',
    transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1), transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)'
  });

  const isOrangeMouthError = hasError;
  const isOrangeMouthPassword = focusedField === 'password' && !showPassword;
  const isOrangeMouthDefault = !isOrangeMouthError && !isOrangeMouthPassword;

  const getOrangeMouthStyle = (isActive) => ({
    opacity: isActive ? 1 : 0,
    transform: isActive ? 'scale(1)' : 'scale(0.4)',
    transformOrigin: '150px 285px',
    transition: 'opacity 0.25s cubic-bezier(0.25, 1, 0.5, 1), transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)'
  });

  const isIdle = focusedField === null && !isLoading && !hasError;

  return (
    <svg viewBox="0 0 400 400" className="w-100 h-100" style={{ maxHeight: '380px', userSelect: 'none' }}>
      {/* Ground line reference is y=320 */}

      {/* 1. Purple Character Group (Backmost) */}
      <g 
        className="svg-group" 
        style={{ 
          transform: purpleTransform, 
          transformOrigin: '160px 320px' 
        }}
      >
        <g className={purpleInnerClass} style={purpleInnerStyle}>
          {/* Purple Body */}
          <rect x="110" y="80" width="100" height="240" rx="24" fill="#a855f7" />
          
          {/* Purple Eyes (White background) */}
          <g className="svg-eye-group-purple">
            <circle cx="140" cy="125" r="11" fill="#fff" />
            <circle cx="180" cy="125" r="11" fill="#fff" />
            
            {/* Purple Pupils */}
            <circle 
              cx="140" 
              cy="125" 
              r="5.5" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-purple-idle' : ''}`} 
              style={{ transform: `translate(${purpleEyeOffsetLeft}px, ${purpleEyeOffsetTop}px)` }} 
            />
            <circle 
              cx="180" 
              cy="125" 
              r="5.5" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-purple-idle' : ''}`} 
              style={{ transform: `translate(${purpleEyeOffsetLeft}px, ${purpleEyeOffsetTop}px)` }} 
            />
          </g>
          
          {/* Purple Mouth shapes */}
          {/* 1. Error mouth */}
          <path 
            d="M 150 148 Q 160 142 170 148" 
            stroke="#000" 
            strokeWidth="3" 
            fill="none" 
            strokeLinecap="round" 
            style={getPurpleMouthStyle(isPurpleMouthError)}
          />
          {/* 2. Loading mouth */}
          <circle 
            cx="160" 
            cy="148" 
            r="7" 
            fill="#000" 
            style={getPurpleMouthStyle(isPurpleMouthLoading)}
          />
          {/* 3. Password typing (masked) mouth */}
          <rect 
            x="158" 
            y="142" 
            width="5" 
            height="18" 
            rx="2.5" 
            fill="#000" 
            style={getPurpleMouthStyle(isPurpleMouthPasswordTyping)}
          />
          {/* 4. Show password (lookaway) mouth */}
          <path 
            d="M 152 153 Q 160 146 168 153" 
            stroke="#000" 
            strokeWidth="3" 
            fill="none" 
            strokeLinecap="round" 
            style={getPurpleMouthStyle(isPurpleMouthShowPassword)}
          />
          {/* 5. Default mouth */}
          <path 
            d="M 152 147 Q 160 154 168 147" 
            stroke="#000" 
            strokeWidth="3" 
            fill="none" 
            strokeLinecap="round" 
            style={getPurpleMouthStyle(isPurpleMouthDefault)}
          />
        </g>
      </g>

      {/* 2. Black Character Group (Middle) */}
      <g 
        className="svg-group" 
        style={{ 
          transform: blackTransform, 
          transformOrigin: '245px 320px' 
        }}
      >
        <g className={blackInnerClass} style={blackInnerStyle}>
          {/* Black Body */}
          <rect x="210" y="140" width="70" height="180" rx="18" fill="#1e293b" />
          
          {/* Black Eyes */}
          <g className="svg-eye-group-black">
            <circle cx="230" cy="180" r="8.5" fill="#fff" />
            <circle cx="260" cy="180" r="8.5" fill="#fff" />
            
            {/* Black Pupils */}
            <circle 
              cx="230" 
              cy="180" 
              r="4" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-black-idle' : ''}`} 
              style={{ transform: `translate(${blackEyeOffsetLeft}px, ${blackEyeOffsetTop}px)` }} 
            />
            <circle 
              cx="260" 
              cy="180" 
              r="4" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-black-idle' : ''}`} 
              style={{ transform: `translate(${blackEyeOffsetLeft}px, ${blackEyeOffsetTop}px)` }} 
            />
          </g>
          
          {/* Black Mouth shapes */}
          <line 
            x1="239" 
            y1="198" 
            x2="251" 
            y2="198" 
            stroke="#fff" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            style={getBlackMouthStyle(hasError)}
          />
          <path 
            d="M 239 196 Q 245 201 251 196" 
            stroke="#fff" 
            strokeWidth="2.5" 
            fill="none" 
            strokeLinecap="round" 
            style={getBlackMouthStyle(!hasError)}
          />
        </g>
      </g>

      {/* 3. Yellow Character Group (Front Right) */}
      <g 
        className="svg-group" 
        style={{ 
          transform: yellowTransform, 
          transformOrigin: '310px 320px' 
        }}
      >
        <g className={yellowInnerClass} style={yellowInnerStyle}>
          {/* Yellow Body */}
          <path d="M 270 320 C 270 210, 270 190, 305 190 C 340 190, 350 210, 350 320 Z" fill="#eab308" />
          
          {/* Yellow Eye */}
          <circle cx="315" cy="225" r="6" fill="#000" className="svg-eye-yellow" />
          
          {/* Yellow Beak shapes */}
          <path 
            d="M 327 238 Q 338 230 357 238" 
            stroke="#000" 
            strokeWidth="5.5" 
            strokeLinecap="round" 
            fill="none" 
            className="svg-beak" 
            style={getYellowBeakStyle(isYellowBeakError)}
          />
          <path 
            d="M 327 233 Q 338 245 357 233" 
            stroke="#000" 
            strokeWidth="5.5" 
            strokeLinecap="round" 
            fill="none" 
            className="svg-beak" 
            style={getYellowBeakStyle(isYellowBeakPassword)}
          />
          <line 
            x1="327" 
            y1="233" 
            x2="357" 
            y2="233" 
            stroke="#000" 
            strokeWidth="5.5" 
            strokeLinecap="round" 
            className="svg-beak" 
            style={getYellowBeakStyle(isYellowBeakDefault)}
          />
        </g>
      </g>

      {/* 4. Orange Character Group (Front Left) */}
      <g 
        className="svg-group" 
        style={{ 
          transform: orangeTransform, 
          transformOrigin: '130px 320px' 
        }}
      >
        <g className={orangeInnerClass} style={orangeInnerStyle}>
          {/* Orange Body */}
          <path d="M 20 320 C 20 180, 240 180, 240 320 Z" fill="#f97316" />
          
          {/* Orange Eyes */}
          {/* Round Pupil Eyes */}
          <g 
            className={showPassword ? "" : "svg-eye-group-orange-blinking"}
            style={{ 
              opacity: showPassword ? 0 : 1, 
              transform: showPassword ? 'scaleY(0)' : 'scaleY(1)',
              transformOrigin: '150px 265px',
              transition: 'opacity 0.25s ease, transform 0.25s ease'
            }}
          >
            <circle 
              cx="135" 
              cy="265" 
              r="7" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-orange-idle' : ''}`} 
              style={{ transform: `translate(${orangeEyeOffsetLeft}px, ${orangeEyeOffsetTop}px)` }} 
            />
            <circle 
              cx="165" 
              cy="265" 
              r="7" 
              fill="#000" 
              className={`svg-eye-pupil ${isIdle ? 'pupil-orange-idle' : ''}`} 
              style={{ transform: `translate(${orangeEyeOffsetLeft}px, ${orangeEyeOffsetTop}px)` }} 
            />
          </g>
          {/* Closed Arch Eyes */}
          <g 
            style={{ 
              opacity: showPassword ? 1 : 0, 
              transform: showPassword ? 'scaleY(1)' : 'scaleY(0)',
              transformOrigin: '150px 265px',
              transition: 'opacity 0.25s ease, transform 0.25s ease'
            }}
          >
            <path d="M 122 268 Q 130 259 138 268" stroke="#000" strokeWidth="4.5" fill="none" strokeLinecap="round" />
            <path d="M 162 268 Q 170 259 178 268" stroke="#000" strokeWidth="4.5" fill="none" strokeLinecap="round" />
          </g>
          
          {/* Orange Mouth shapes */}
          <path 
            d="M 144 288 Q 150 281 156 288" 
            stroke="#000" 
            strokeWidth="3.5" 
            fill="none" 
            strokeLinecap="round" 
            style={getOrangeMouthStyle(isOrangeMouthError)}
          />
          <circle 
            cx="150" 
            cy="285" 
            r="5.5" 
            fill="#000" 
            style={getOrangeMouthStyle(isOrangeMouthPassword)}
          />
          <path 
            d="M 145 285 Q 150 292 155 285" 
            stroke="#000" 
            strokeWidth="3.5" 
            fill="none" 
            strokeLinecap="round" 
            style={getOrangeMouthStyle(isOrangeMouthDefault)}
          />
        </g>
      </g>
    </svg>
  );
};

const styleMarkup = `
  .svg-group {
    transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    transform-box: fill-box;
  }
  .svg-eye-pupil {
    transition: transform 0.25s ease-out;
    transform-box: fill-box;
    transform-origin: center;
  }
  @keyframes purple-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px) scaleY(1.06); }
  }
  @keyframes orange-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px) scaleY(1.04); }
  }
  @keyframes black-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-16px) scaleY(1.05); }
  }
  @keyframes yellow-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px) scaleY(1.03); }
  }
  .purple-loading {
    animation: purple-bounce 0.6s infinite ease-in-out;
  }
  .orange-loading {
    animation: orange-bounce 0.6s infinite ease-in-out 0.15s;
  }
  .black-loading {
    animation: black-bounce 0.6s infinite ease-in-out 0.3s;
  }
  .yellow-loading {
    animation: yellow-bounce 0.6s infinite ease-in-out 0.45s;
  }
  @keyframes error-shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-6px) rotate(-1.5deg); }
    40%, 80% { transform: translateX(6px) rotate(1.5deg); }
  }
  .error-shaking {
    animation: error-shake 0.35s ease-in-out;
  }

  /* Continuous Idle Breathing Animations */
  @keyframes purple-idle {
    0%, 100% { transform: translateY(0) scaleY(1); }
    50% { transform: translateY(-5px) scaleY(1.015); }
  }
  @keyframes black-idle {
    0%, 100% { transform: translateY(0) scaleY(1); }
    50% { transform: translateY(-4px) scaleY(1.015); }
  }
  @keyframes yellow-idle {
    0%, 100% { transform: translateY(0) scaleY(1); }
    50% { transform: translateY(-3px) scaleY(1.01); }
  }
  @keyframes orange-idle {
    0%, 100% { transform: translateY(0) scaleY(1); }
    50% { transform: translateY(-2px) scaleY(1.015); }
  }
  .purple-idle {
    animation: purple-idle 4s infinite ease-in-out;
  }
  .black-idle {
    animation: black-idle 4.2s infinite ease-in-out 0.3s;
  }
  .yellow-idle {
    animation: yellow-idle 4.5s infinite ease-in-out 0.6s;
  }
  .orange-idle {
    animation: orange-idle 3.8s infinite ease-in-out 0.15s;
  }

  /* Blinking Animations */
  @keyframes eye-blink {
    0%, 96%, 100% { transform: scaleY(1); }
    98% { transform: scaleY(0.08); }
  }
  .svg-eye-group-purple {
    animation: eye-blink 5s infinite;
    transform-box: fill-box;
    transform-origin: center;
  }
  .svg-eye-group-black {
    animation: eye-blink 6.2s infinite 1.2s;
    transform-box: fill-box;
    transform-origin: center;
  }
  .svg-eye-yellow {
    animation: eye-blink 5.5s infinite 1.8s;
    transform-box: fill-box;
    transform-origin: center;
  }
  .svg-eye-group-orange-blinking {
    animation: eye-blink 4.8s infinite 2.2s;
    transform-box: fill-box;
    transform-origin: center;
  }

  /* Look-around Animations */
  @keyframes look-around-purple {
    0%, 100% { transform: translate(2px, -1px); }
    20%, 25% { transform: translate(5px, -2px); }
    45%, 50% { transform: translate(-2px, 0px); }
    70%, 75% { transform: translate(4px, 1px); }
  }
  @keyframes look-around-black {
    0%, 100% { transform: translate(2px, -1px); }
    20%, 25% { transform: translate(-2px, 1px); }
    45%, 50% { transform: translate(4px, -2px); }
    70%, 75% { transform: translate(1px, 2px); }
  }
  @keyframes look-around-orange {
    0%, 100% { transform: translate(2px, -2px); }
    20%, 25% { transform: translate(5px, -1px); }
    45%, 50% { transform: translate(-2px, -3px); }
    70%, 75% { transform: translate(3px, 0px); }
  }
  .pupil-purple-idle {
    animation: look-around-purple 6.5s infinite ease-in-out;
  }
  .pupil-black-idle {
    animation: look-around-black 7.2s infinite ease-in-out 0.8s;
  }
  .pupil-orange-idle {
    animation: look-around-orange 6s infinite ease-in-out 1.5s;
  }
`;

function LoginPage({ onLogin }) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLaunchDemo = () => {
    sessionStorage.setItem('isDemoMode', 'true');
    onLogin({
      id: 'mock-user-01',
      username: 'demo_admin',
      role: 'admin',
      permissions: []
    }, 'mock-jwt-token-for-demo');
    navigate('/machines');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.username.trim() || !credentials.password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await authService.login(credentials);
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      onLogin(data.user, data.token);
      navigate('/machines');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center justify-content-center p-3"
      style={{
        backgroundColor: 'var(--bg-main)',
      }}
    >
      <style>{styleMarkup}</style>
      <Container className="d-flex align-items-center justify-content-center" style={{ maxWidth: '900px' }}>
        <Card 
          className="border-0 w-100" 
          style={{ 
            borderRadius: '24px', 
            overflow: 'hidden', 
            backgroundColor: 'var(--bg-card)', 
            border: '1px solid var(--border-color)' 
          }}
        >
          <div className="row g-0">
            {/* Left Column: Interactive Vector Illustration */}
            <div 
              className="col-md-6 d-none d-md-flex flex-column align-items-center justify-content-center p-4" 
              style={{ 
                backgroundColor: 'var(--bg-main)', 
                borderRight: '1px solid var(--border-color)' 
              }}
            >
              <RenderIllustration 
                focusedField={focusedField} 
                showPassword={showPassword} 
                passwordLength={credentials.password.length} 
                usernameLength={credentials.username.length}
                isLoading={isLoading}
                hasError={!!error}
              />
            </div>

            {/* Right Column: Form */}
            <div className="col-md-6 p-4 p-lg-5 d-flex flex-column justify-content-center">
              <div className="mb-4">
                <span className="d-inline-block mb-1 text-theme-highlight" style={{ fontSize: '1.5rem', lineHeight: 1 }}>✦</span>
                <h2 className="text-theme-main font-bold mb-1" style={{ fontSize: '1.8rem', letterSpacing: '-0.025em' }}>Welcome back!</h2>
                <p className="text-theme-muted" style={{ fontSize: '0.875rem' }}>Please enter your details</p>
              </div>

              {error && (
                <Alert variant="danger" className="py-2 px-3 rounded-lg border-0 mb-4" style={{ background: 'rgba(244, 63, 94, 0.12)', color: 'var(--color-danger)', fontSize: '0.85rem' }}>
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={credentials.username}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('username')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter username"
                    disabled={isLoading}
                    required
                    style={{
                      border: 'none',
                      borderBottom: `2px solid ${focusedField === 'username' ? 'var(--color-primary)' : 'var(--border-color)'}`,
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      color: 'var(--text-main)',
                      boxShadow: 'none',
                      paddingLeft: '0',
                      paddingRight: '0',
                      fontSize: '0.95rem',
                      transition: 'border-color 0.15s ease'
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Password</Form.Label>
                  <div className="position-relative d-flex align-items-center">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="••••••••"
                      disabled={isLoading}
                      required
                      style={{
                        border: 'none',
                        borderBottom: `2px solid ${focusedField === 'password' ? 'var(--color-primary)' : 'var(--border-color)'}`,
                        borderRadius: '0',
                        backgroundColor: 'transparent',
                        color: 'var(--text-main)',
                        boxShadow: 'none',
                        paddingLeft: '0',
                        paddingRight: '30px',
                        fontSize: '0.95rem',
                        transition: 'border-color 0.15s ease'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="position-absolute border-0 bg-transparent text-theme-muted p-0"
                      style={{ right: '0', bottom: '8px', zIndex: 10, outline: 'none' }}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} style={{ fontSize: '0.9rem' }}></i>
                    </button>
                  </div>
                </Form.Group>

                <div className="d-flex align-items-center justify-content-between mb-4" style={{ fontSize: '0.85rem' }}>
                  <Form.Check
                    type="checkbox"
                    id="remember-me"
                    label="Remember for 30 days"
                    className="text-theme-muted"
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="text-theme-muted" style={{ cursor: 'pointer' }}>Forgot password?</span>
                </div>

                <Button 
                  type="submit" 
                  className="btn-premium-primary w-100 py-2.5" 
                  disabled={isLoading}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" animation="border" />
                      Signing in...
                    </>
                  ) : (
                    'Log in'
                  )}
                </Button>

                <Button 
                  variant="link"
                  className="btn-premium-secondary w-100 py-2.5 mt-2" 
                  style={{ borderRadius: '8px', color: 'var(--text-main)', border: '1px solid var(--border-color)' }}
                  disabled
                >
                  <i className="fab fa-google me-2 text-danger"></i>
                  Log in with Google
                </Button>

                <div className="text-center my-3">
                  <span className="text-theme-muted" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1.2px' }}>Or Sandbox Mode</span>
                </div>

                <Button 
                  type="button"
                  onClick={handleLaunchDemo}
                  className="btn-premium-secondary w-100 py-2.5" 
                  style={{ borderRadius: '8px', color: 'var(--text-highlight)', border: '1px solid var(--color-primary)', background: 'rgba(99, 102, 241, 0.05)', fontWeight: 600 }}
                >
                  <i className="fas fa-play-circle me-2 text-primary"></i>
                  View Demo
                </Button>
              </Form>

              <div className="text-center mt-4" style={{ fontSize: '0.85rem' }}>
                <span className="text-theme-muted">Don't have an account? </span>
                <span className="text-theme-main font-bold" style={{ cursor: 'pointer' }}>Sign up</span>
              </div>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
}

export default LoginPage;